import React, { useEffect, useState } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const formatDateTime = (value) => {
  if (!value) return 'N/A'
  try {
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    return value
  }
}

const ReturnCode = () => {
  const [returnCodes, setReturnCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchReturnCodes = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await apiClient.get(API_ENDPOINTS.SALES_RETURNS_CODE)
        const data = Array.isArray(response.data) ? response.data : response.data?.results || []
        setReturnCodes(data)
      } catch (err) {
        console.error('Error fetching return codes:', err)
        setError(err.response?.data?.detail || err.response?.data?.message || 'Failed to load return codes. Please try again.')
        setReturnCodes([])
      } finally {
        setLoading(false)
      }
    }

    fetchReturnCodes()
  }, [])

  return (
    <>
      <div className="pagetitle">
        <h1>Return Codes</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Sales</li>
            <li className="breadcrumb-item active">Return Codes</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1">Active Return Codes</h5>
                    <p className="text-muted mb-0">View and manage all return codes for sales refunds</p>
                  </div>
                  {!loading && returnCodes.length > 0 && (
                    <span className="badge bg-primary fs-6 px-3 py-2">
                      {returnCodes.length} {returnCodes.length === 1 ? 'Code' : 'Codes'}
                    </span>
                  )}
                </div>

                {loading && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-3">Loading return codes...</p>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Error:</strong> {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                  </div>
                )}

                {!loading && !error && returnCodes.length === 0 && (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#dee2e6' }}></i>
                    <p className="text-muted mt-3 mb-0">No return codes found</p>
                  </div>
                )}

                {!loading && !error && returnCodes.length > 0 && (
                  <div className="row g-4">
                    {returnCodes.map((code) => (
                      <div key={code.id} className="col-lg-6 col-xl-4">
                        <div className="card border-0 shadow-sm h-100" style={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '15px',
                          overflow: 'hidden',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)'
                          e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)'
                        }}>
                          <div className="card-body text-white p-4">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <h6 className="text-white-50 mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                  Return Code
                                </h6>
                                <h3 className="mb-0 fw-bold" style={{ fontSize: '2rem', letterSpacing: '2px' }}>
                                  {code.code}
                                </h3>
                              </div>
                              <div className="text-end">
                                {code.is_active && !code.is_expired ? (
                                  <span className="badge bg-success bg-opacity-75 px-3 py-2">
                                    <i className="bi bi-check-circle me-1"></i>Active
                                  </span>
                                ) : code.is_expired ? (
                                  <span className="badge bg-danger bg-opacity-75 px-3 py-2">
                                    <i className="bi bi-x-circle me-1"></i>Expired
                                  </span>
                                ) : (
                                  <span className="badge bg-secondary bg-opacity-75 px-3 py-2">
                                    <i className="bi bi-pause-circle me-1"></i>Inactive
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="mt-4 pt-3 border-top border-white border-opacity-25">
                              <div className="mb-3">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="bi bi-shop me-2" style={{ fontSize: '1.1rem' }}></i>
                                  <span className="text-white-50" style={{ fontSize: '0.85rem' }}>Branch</span>
                                </div>
                                <div className="fw-semibold" style={{ fontSize: '1rem' }}>
                                  {code.branch_name || `Branch ${code.branch}`}
                                </div>
                              </div>

                              <div className="mb-3">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="bi bi-calendar-event me-2" style={{ fontSize: '1.1rem' }}></i>
                                  <span className="text-white-50" style={{ fontSize: '0.85rem' }}>Expires At</span>
                                </div>
                                <div className="fw-semibold" style={{ fontSize: '0.95rem' }}>
                                  {formatDateTime(code.expires_at)}
                                </div>
                              </div>

                              <div className="mb-2">
                                <div className="d-flex align-items-center mb-2">
                                  <i className="bi bi-clock-history me-2" style={{ fontSize: '1.1rem' }}></i>
                                  <span className="text-white-50" style={{ fontSize: '0.85rem' }}>Created</span>
                                </div>
                                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                  {formatDateTime(code.created_at)}
                                </div>
                              </div>

                              {code.created_by && (
                                <div className="mt-3 pt-3 border-top border-white border-opacity-25">
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-person me-2" style={{ fontSize: '1.1rem' }}></i>
                                    <span className="text-white-50" style={{ fontSize: '0.85rem' }}>
                                      Created by: <span className="text-white fw-semibold">{code.created_by}</span>
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ReturnCode


